// Folder colors: Nordic pastels, desaturated to sit on warm paper.
// Ids are persisted (items.content.color, boards.icon_color) and mirrored in
// the partnr-chat operator tools, so change values only, never ids.
// Each color includes a main color and a complementary label background/text combo

export interface FolderColorPalette {
  id: string;
  name: string;
  folder: string;      // Main folder color (HSL)
  folderDark: string;  // Slightly darker shade for depth
  label: string;       // Label background color
  labelText: string;   // Label text color
}

export const FOLDER_COLORS: FolderColorPalette[] = [
  {
    id: 'periwinkle',
    name: 'Periwinkle',
    folder: 'hsl(225, 32%, 76%)',
    folderDark: 'hsl(225, 26%, 64%)',
    label: 'hsl(45, 45%, 88%)',
    labelText: 'hsl(225, 25%, 24%)',
  },
  {
    id: 'sage',
    name: 'Sage',
    folder: 'hsl(130, 18%, 72%)',
    folderDark: 'hsl(130, 15%, 60%)',
    label: 'hsl(40, 40%, 89%)',
    labelText: 'hsl(130, 18%, 22%)',
  },
  {
    id: 'coral',
    name: 'Coral',
    folder: 'hsl(12, 42%, 76%)',
    folderDark: 'hsl(12, 34%, 64%)',
    label: 'hsl(42, 50%, 89%)',
    labelText: 'hsl(12, 35%, 26%)',
  },
  {
    id: 'lavender',
    name: 'Lavender',
    folder: 'hsl(265, 22%, 78%)',
    folderDark: 'hsl(265, 18%, 66%)',
    label: 'hsl(50, 45%, 89%)',
    labelText: 'hsl(265, 20%, 28%)',
  },
  {
    id: 'mint',
    name: 'Mint',
    folder: 'hsl(165, 22%, 72%)',
    folderDark: 'hsl(165, 18%, 60%)',
    label: 'hsl(40, 45%, 89%)',
    labelText: 'hsl(165, 22%, 22%)',
  },
  {
    id: 'butter',
    name: 'Butter',
    folder: 'hsl(46, 55%, 78%)',
    folderDark: 'hsl(46, 45%, 66%)',
    label: 'hsl(30, 40%, 88%)',
    labelText: 'hsl(30, 35%, 24%)',
  },
  {
    id: 'rose',
    name: 'Rose',
    folder: 'hsl(350, 30%, 79%)',
    folderDark: 'hsl(350, 24%, 67%)',
    label: 'hsl(48, 50%, 89%)',
    labelText: 'hsl(350, 25%, 28%)',
  },
  {
    id: 'sky',
    name: 'Sky',
    folder: 'hsl(205, 34%, 76%)',
    folderDark: 'hsl(205, 28%, 64%)',
    label: 'hsl(40, 45%, 89%)',
    labelText: 'hsl(205, 28%, 24%)',
  },
  {
    id: 'peach',
    name: 'Peach',
    folder: 'hsl(25, 45%, 79%)',
    folderDark: 'hsl(25, 38%, 67%)',
    label: 'hsl(50, 50%, 90%)',
    labelText: 'hsl(25, 32%, 26%)',
  },
  {
    id: 'slate',
    name: 'Slate',
    folder: 'hsl(210, 12%, 72%)',
    folderDark: 'hsl(210, 10%, 60%)',
    label: 'hsl(45, 35%, 89%)',
    labelText: 'hsl(210, 12%, 24%)',
  },
];

// Values from before the Nordic paper palette. Rows written by older code may
// hold the folder color string instead of the id; map them back to the id.
const LEGACY_FOLDER_VALUES: Record<string, string> = {
  'hsl(225, 60%, 75%)': 'periwinkle',
  'hsl(140, 40%, 70%)': 'sage',
  'hsl(15, 70%, 75%)': 'coral',
  'hsl(270, 50%, 78%)': 'lavender',
  'hsl(165, 50%, 72%)': 'mint',
  'hsl(48, 80%, 78%)': 'butter',
  'hsl(340, 55%, 78%)': 'rose',
  'hsl(200, 65%, 75%)': 'sky',
  'hsl(25, 75%, 78%)': 'peach',
  'hsl(210, 25%, 70%)': 'slate',
};

// Get a random folder color palette
export function getRandomFolderColor(): FolderColorPalette {
  const randomIndex = Math.floor(Math.random() * FOLDER_COLORS.length);
  return FOLDER_COLORS[randomIndex];
}

// Get folder color by ID (or return default periwinkle)
export function getFolderColorById(id: string | undefined): FolderColorPalette {
  if (!id) return FOLDER_COLORS[0];
  return FOLDER_COLORS.find(c => c.id === id) || FOLDER_COLORS[0];
}

// Get folder color from hex/hsl string (tries to match closest)
export function getFolderColorFromValue(value: string | undefined): FolderColorPalette {
  if (!value) return FOLDER_COLORS[0];
  
  // First try to find by ID
  const byId = FOLDER_COLORS.find(c => c.id === value);
  if (byId) return byId;
  
  // Then try to find by folder color
  const byColor = FOLDER_COLORS.find(c => c.folder === value);
  if (byColor) return byColor;

  const legacyId = LEGACY_FOLDER_VALUES[value];
  if (legacyId) return getFolderColorById(legacyId);
  
  // Default to first
  return FOLDER_COLORS[0];
}
